// ============================================================================
//  TUTORIAL OVERLAY - Complete App Walkthrough with Page Navigation

// For new users: Full flow through Home → Wallet → Tables → Create Table → Voice/Video → Profile/Support → Complete
// For agents: Separate agent dashboard tutorial (triggered from profile)
// ============================================================================
//  ENHANCED: Added Create Table walkthrough (8 steps)

//  ENHANCED: Added Voice & Video Chat section (2 steps - premium feature with fee info)

//  ENHANCED: Added SUPPORT emphasis section - NOW NAVIGATES TO PROFILE!

//  ENHANCED: Added Beta notice step in wallet section

//  FIX: Support button now properly highlighted in Profile page

// ============================================================================

import { hideSlots } from '../../utils/storeSafe';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Text, VStack, HStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { auth } from '../../services/firebase';
import { tutorialService, TutorialType } from '../../services/tutorialService';

// ============================================================================
// ANIMATIONS
// ============================================================================

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 217, 217, 0.6); }
  50% { box-shadow: 0 0 0 12px rgba(0, 217, 217, 0); }
`;

const bounceIn = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); opacity: 1; }
`;

const confettiFall = keyframes`
  0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

const waveHand = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(20deg); }
  75% { transform: rotate(-10deg); }
`;

const alertPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// ============================================================================
// SOUND
// ============================================================================

/**
 * First VISIBLE match for a selector. Several anchors exist twice - a
 * desktop render and a mobile render, one of them display:none - and plain
 * querySelector happily returns the hidden one, which measures 0x0 and
 * puts the spotlight on nothing.
 */
const findVisibleTarget = (selector: string): HTMLElement | null => {
  const candidates = document.querySelectorAll<HTMLElement>(selector);
  for (let i = 0; i < candidates.length; i++) {
    const r = candidates[i].getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return candidates[i];
  }
  return null;
};

const playWinSound = () => {
  try {
    const audio = new Audio('/sounds/you-win-sequence-2-183949.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {}
};

// ============================================================================
// CHIP MASCOT
// ============================================================================

interface ChipProps {
  expression?: 'happy' | 'excited' | 'waving' | 'pointing' | 'celebrating' | 'thinking' | 'alert';
  size?: number;
}

const ChipMascot: React.FC<ChipProps> = ({ expression = 'happy', size = 70 }) => {
  const getEmoji = () => {
    switch (expression) {
      case 'excited': return '';

      case 'waving': return '';

      case 'pointing': return '';

      case 'celebrating': return '';

      case 'thinking': return '';

      case 'alert': return '';

      default: return '';

    }
  };

  return (
    <Box
      w={`${size}px`}
      h={`${size}px`}
      borderRadius="full"
      bg="linear-gradient(145deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)"
      border="4px solid #B8860B"
      boxShadow="0 4px 20px rgba(255, 165, 0, 0.5), inset 0 2px 10px rgba(255,255,255,0.3)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
      animation={expression === 'alert' ? `${alertPulse} 0.5s ease-in-out infinite` : `${float} 2s ease-in-out infinite`}
      flexShrink={0}
    >
      <Box
        position="absolute"
        top="10%"
        left="10%"
        right="10%"
        bottom="10%"
        borderRadius="full"
        border="3px dashed rgba(139, 69, 19, 0.4)"
      />
      <Text fontSize={size * 0.4}>{getEmoji()}</Text>
      
      {expression === 'waving' && (
        <Box
          position="absolute"
          right="-8px"
          bottom="5px"
          fontSize={size * 0.28}
          animation={`${waveHand} 0.5s ease-in-out infinite`}
          transformOrigin="bottom center"
        >
          

        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// CONFETTI
// ============================================================================

const Confetti: React.FC = () => {
  const colors = ['#FFD700', '#00D9D9', '#FF6B6B', '#4ECDC4', '#FF8C00', '#22c55e'];
  const pieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 1,
    duration: 2 + Math.random() * 2,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: 6 + Math.random() * 10,
  }));

  return (
    <Box position="fixed" inset={0} pointerEvents="none" zIndex={100002} overflow="hidden">
      {pieces.map(p => (
        <Box
          key={p.id}
          position="absolute"
          top="-20px"
          left={`${p.left}%`}
          w={`${p.size}px`}
          h={`${p.size}px`}
          bg={p.color}
          borderRadius={Math.random() > 0.5 ? 'full' : '2px'}
          animation={`${confettiFall} ${p.duration}s linear ${p.delay}s forwards`}
        />
      ))}
    </Box>
  );
};

// ============================================================================
// TUTORIAL STEP INTERFACE
// ============================================================================

export interface TutorialStep {
  target?: string;           // CSS selector - if empty, centers on screen
  title: string;
  content: string;
  chipExpression: ChipProps['expression'];
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  icon?: string;
  navigateTo?: string;       // Navigate to this route before showing step
  waitForElement?: boolean;  // Wait for target element to appear after navigation
  isImportant?: boolean;     //  NEW: Flag for critical steps (like Support)

}

// ============================================================================
// TUTORIAL SECTIONS - the jump bar above the progress dots. startIndex MUST
// track MAIN_TUTORIAL_STEPS: this constant was lost in the 51-to-17 step
// rewrite while its UI survived, and the dangling reference white-screened
// the whole app the moment the tour opened.
// ============================================================================

// Store-safe builds (iPhone) have no slot games: that chapter and its step
// are dropped and the later sections shift up by one.
const STORE_SAFE_TUTORIAL = hideSlots();
const TUTORIAL_SECTIONS: Array<{ name: string; icon: string; startIndex: number }> = [
  { name: 'Start',    icon: '★', startIndex: 0 },
  { name: 'Wallet',   icon: '$', startIndex: 1 },
  { name: 'Tables',   icon: '♠', startIndex: 5 },
  { name: 'Events',   icon: '♛', startIndex: 11 },
  { name: 'Training', icon: '◆', startIndex: 12 },
  { name: 'Rewards',  icon: '✦', startIndex: 14 },
  ...(STORE_SAFE_TUTORIAL ? [] : [{ name: 'Slots', icon: '⑦', startIndex: 17 }]),
  { name: 'Support',  icon: '✚', startIndex: STORE_SAFE_TUTORIAL ? 17 : 18 },
  { name: 'Done',     icon: '✓', startIndex: STORE_SAFE_TUTORIAL ? 18 : 19 },
];

/** The section a given step index belongs to (last section whose start <= step). */
const getSectionForStep = (step: number) => {
  let current = TUTORIAL_SECTIONS[0];
  for (const s of TUTORIAL_SECTIONS) {
    if (step >= s.startIndex) current = s;
  }
  return current;
};

// ============================================================================
// MAIN TUTORIAL - Complete new user walkthrough
// Covers: Home → Training → Wallet (with Beta notice) → Tables → Create Table → SUPPORT → Complete
// ============================================================================

const MAIN_TUTORIAL_STEPS_ALL: TutorialStep[] = [
  // Eighteen stops, ~90 seconds: every important room plus the money path
  // (top-up and prize redemption). The old 51-step version toured every
  // button in the app; nobody finished it. This one shows the rooms, not
  // the furniture - and ends on a reward, which is why people finish.
  {
    title: 'Welcome to PokerSweepStars',
    content: "Sixty seconds, a few stops - where to play, where to win, and the free stuff you should never leave on the table. Let's go.",
    chipExpression: 'waving',
    position: 'center',
  },
  {
    target: '[data-tutorial="wallet-balance"]',
    title: 'Two currencies, one wallet',
    content: 'PlayCoins are for fun - you start with a pile and the app keeps topping you up. PSS Coins are the sweepstakes currency real prizes ride on.',
    chipExpression: 'pointing',
    position: 'bottom',
    navigateTo: '/wallet',
    waitForElement: true,
  },
  // The money path. Careful wording: PlayCoin packages are what is
  // purchased; PSS Coins come included free - that framing is the
  // sweepstakes model, not marketing copy.
  {
    target: '[data-tutorial="buy-coins"]',
    title: 'Topping up',
    content: 'Run low? PlayCoin packages start small - and every package includes bonus PSS Coins, free.',
    chipExpression: 'pointing',
    position: 'bottom',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="withdraw"]',
    title: 'Real prizes, right here',
    content: 'PSS Coin winnings redeem for real prizes from this button once playthrough clears - the wallet always shows exactly what is redeemable.',
    chipExpression: 'happy',
    position: 'bottom',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="referrals"]',
    title: 'Bring a friend, both get paid',
    content: 'Your referral link lives here. Friends who join and play earn you both a bonus.',
    chipExpression: 'happy',
    position: 'bottom',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="quick-match-button"]',
    title: 'The fastest way in',
    content: 'Quick Match finds you a seat at the right stakes in one tap. When in doubt, start here.',
    chipExpression: 'excited',
    position: 'bottom',
    navigateTo: '/',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="table-list"]',
    title: 'The felt is always warm',
    content: 'Cash tables run around the clock. Tap one, pick a buy-in, take a seat - and leave whenever. Your chips walk away with you.',
    chipExpression: 'excited',
    position: 'top',
    navigateTo: '/tables',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="create-table"]',
    title: 'Your table, your rules',
    content: 'Create a private table, set the stakes and rules, and share the code - poker night with your own people.',
    chipExpression: 'happy',
    position: 'bottom',
    waitForElement: true,
  },
  // ── The practice felt (/tour/table) - a mock table, all static data, so
  // the tour can point at seats and cards with zero live-game risk. ──
  {
    target: '[data-tutorial="mock-table-felt"]',
    title: 'Reading the felt',
    content: 'A practice view - nothing here is live. Community cards in the middle, the pot beneath them, your cards down front. You are holding ace-king of hearts, by the way. Nice.',
    chipExpression: 'pointing',
    position: 'bottom',
    navigateTo: '/tour/table',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="mock-table-bots"]',
    title: 'Never an empty room',
    content: 'Seats marked BOT are house players. They post blinds, play straight, and never sleep - so there is always a game, even at 4am.',
    chipExpression: 'happy',
    position: 'bottom',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="mock-table-types"]',
    title: 'Four kinds of table',
    content: 'PlayCoins to play free, PSS Coins where the prizes ride, Private for your own crew, Tournaments for the big boards.',
    chipExpression: 'excited',
    position: 'top',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="tournaments-list"]',
    title: 'Where the real prizes live',
    content: 'Scheduled events with prize pools, final tables and bragging rights. Freerolls cost nothing to enter - start there.',
    chipExpression: 'excited',
    position: 'bottom',
    navigateTo: '/tournaments',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="training-header"]',
    title: 'Get good, quietly',
    content: 'Drills, scenarios and a coach that shows you what the pros would have done. Nobody watches you practice.',
    chipExpression: 'thinking',
    position: 'bottom',
    navigateTo: '/training',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="training-scenario"]',
    title: 'Scenario practice',
    content: 'Real spots - pot odds, bluffs, position - with instant feedback after every decision.',
    chipExpression: 'happy',
    position: 'bottom',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="pack-drop-card"]',
    title: 'Free twice a week',
    content: 'Play a single hand to unlock your Pack Drop, then pick a crate and watch the reel decide. Up to 100,000 PlayCoins - and real PSS Coins up to $50 - hiding inside.',
    chipExpression: 'excited',
    position: 'top',
    navigateTo: '/',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="daily-spin"]',
    title: 'Spin every day',
    content: 'One free spin a day, and PSS Coins are on the wheel. It takes five seconds and resets at midnight.',
    chipExpression: 'happy',
    position: 'bottom',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="leaderboard"]',
    title: 'Climb the board',
    content: 'Weekly and monthly leaderboards, reset fresh so everyone gets a run at the top.',
    chipExpression: 'excited',
    position: 'top',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="slots-lobby"]',
    title: 'A different kind of spin',
    content: 'Slots, for between hands - same PlayCoins, instant answers.',
    chipExpression: 'excited',
    position: 'bottom',
    navigateTo: '/slots',
    waitForElement: true,
  },
  {
    target: '[data-tutorial="support-hub"]',
    title: 'If anything ever feels off',
    content: 'Real humans answer here. Payments, bugs, a hand that looked wrong - message support and we will make it right.',
    chipExpression: 'alert',
    position: 'top',
    navigateTo: '/profile',
    waitForElement: true,
    isImportant: true,
  },
  {
    title: "You're all set",
    content: 'Your daily bonus is waiting on the home screen - 1,000 PlayCoins, on the house. See you at the tables.',
    chipExpression: 'celebrating',
    position: 'center',
    navigateTo: '/',
  },
];

const MAIN_TUTORIAL_STEPS: TutorialStep[] = STORE_SAFE_TUTORIAL
  ? MAIN_TUTORIAL_STEPS_ALL.filter(s => s.navigateTo !== '/slots')
  : MAIN_TUTORIAL_STEPS_ALL;

const AGENT_TUTORIAL_STEPS: TutorialStep[] = [
  {
    title: "Agent Dashboard ",

    content: "Welcome to your money-making hub! Track referrals, earnings, and grow your network.",
    chipExpression: 'excited',
    position: 'center',
    icon: '',

  },
  {
    target: '[data-tutorial="agent-earnings"]',
    title: "Your Earnings ",

    content: "See your total rake earnings, pending payouts, and monthly progress!",
    chipExpression: 'celebrating',
    position: 'bottom',
    icon: '',

    waitForElement: true,
  },
  {
    target: '[data-tutorial="referral-link"]',
    title: "Your Referral Link ",

    content: "Share this link EVERYWHERE! Every signup earns you commission on their play!",
    chipExpression: 'pointing',
    position: 'bottom',
    icon: '',

  },
  {
    target: '[data-tutorial="referral-list"]',
    title: "Your Network ",

    content: "Track every player you've referred and see how much rake they generate!",
    chipExpression: 'happy',
    position: 'top',
    icon: '',

  },
  {
    target: '[data-tutorial="agent-codes"]',
    title: "Voice/Video Bypass Codes ",

    content: "Create codes to waive the $0.50 voice/video chat fee for your players. They enter these codes when creating a table with voice/video enabled!",
    chipExpression: 'thinking',
    position: 'bottom',
    icon: '',

  },
  {
    target: '[data-tutorial="agent-tax"]',
    title: "Tax Information ",

    content: "Track your earnings for tax purposes. If you earn over $600, you'll need a 1099. Keep your W-9 info updated!",
    chipExpression: 'thinking',
    position: 'top',
    icon: '',

    waitForElement: true,
  },
  {
    target: '[data-tutorial="agent-payout"]',
    title: "Request Payout ",

    content: "Hit the minimum threshold? Request a payout to your PayPal or USDC wallet!",
    chipExpression: 'excited',
    position: 'bottom',
    icon: '',

  },
  {
    title: "Go Get 'Em! ",

    content: "Share your link, grow your network, watch the passive income roll in!",
    chipExpression: 'celebrating',
    position: 'center',
    icon: '',

  },
];

// ============================================================================
// GET STEPS BY TYPE
// ============================================================================

const getTutorialSteps = (type: TutorialType): TutorialStep[] => {
  switch (type) {
    case 'agent':
      return AGENT_TUTORIAL_STEPS;
    case 'main':
    default:
      return MAIN_TUTORIAL_STEPS;
  }
};

// ============================================================================
// MAIN TUTORIAL OVERLAY COMPONENT
// ============================================================================

interface TutorialOverlayProps {
  isOpen: boolean;
  onComplete: () => void;
  onSkip: () => void;
  tutorialType?: TutorialType;
}

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
  isOpen,
  onComplete,
  onSkip,
  tutorialType = 'main',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [dialogPos, setDialogPos] = useState({ top: '50%', left: '50%' });
  const [showConfetti, setShowConfetti] = useState(false);
  const [waitingForElement, setWaitingForElement] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  
  //  FIX: One-shot guard - tutorial cannot restart even if React remounts

  const hasStartedRef = useRef(false);
  
  //  FIX: DOM readiness - wait for page to render

  const [domReady, setDomReady] = useState(false);
  const domCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  //  FIX: Blocked paths - never run on these routes

  const blockedPaths = ['/onboarding', '/login', '/verify-email'];
  const isBlockedPath = blockedPaths.some(path => location.pathname.startsWith(path));

  const steps = getTutorialSteps(tutorialType);
  const currentStep = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const userId = auth.currentUser?.uid;
  
  //  FIX: Track if we've scrolled for this step to prevent scroll loops

  const hasScrolledForStepRef = useRef<number>(-1);
  
  //  FIX: One-shot startup guard

  useEffect(() => {
    if (isOpen && hasStartedRef.current) return;
    if (isOpen) hasStartedRef.current = true;
  }, [isOpen]);
  
  //  FIX: DOM readiness check - wait for home page to render

  useEffect(() => {
    if (isBlockedPath || !isOpen || domReady) return;
    if (domCheckIntervalRef.current) return;
    
    const checkDomReady = () => {
      const isHomePage = location.pathname === '/';
      const hasNavigation = !!document.querySelector('nav, [data-testid="navigation"], .navigation');
      
      if (isHomePage && hasNavigation) {
        setDomReady(true);
        if (domCheckIntervalRef.current) {
          clearInterval(domCheckIntervalRef.current);
          domCheckIntervalRef.current = null;
        }
      }
    };
    
    checkDomReady();
    
    if (!domReady) {
      domCheckIntervalRef.current = setInterval(checkDomReady, 200);
      const timeout = setTimeout(() => {
        if (domCheckIntervalRef.current) {
          clearInterval(domCheckIntervalRef.current);
          domCheckIntervalRef.current = null;
        }
        if (location.pathname === '/') setDomReady(true);
      }, 3000);
      
      return () => {
        if (domCheckIntervalRef.current) {
          clearInterval(domCheckIntervalRef.current);
          domCheckIntervalRef.current = null;
        }
        clearTimeout(timeout);
      };
    }
  }, [isOpen, location.pathname, domReady, isBlockedPath]);

  // ==========================================================================
  // Find and highlight target element
  //  FIX: Split into measure-only (for scroll handler) and scroll (for step change)

  // ==========================================================================
  
  // 📱 PHONE PLACEMENT: on a narrow screen there is simply nowhere to put a
  // ~380px card relative to a target - the old maths ran out of room, fell
  // back to clamping at maxTop, and dropped the card straight on top of the
  // thing it was pointing at. Phones instead DOCK the card to the bottom of
  // the screen and the target is scrolled into the space above it, so the
  // highlight is always visible. Returns null on desktop (keep old maths).
  const phoneDockedPos = useCallback((): { top: string; left: string } | null => {
    if (window.innerWidth > 820) return null;
    const measured = dialogRef.current?.getBoundingClientRect().height;
    const dialogH = Math.min(measured && measured > 80 ? measured : 380, window.innerHeight * 0.55);
    const safeBottom = 12 + (parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0'
    ) || 0);
    return {
      top: `${Math.max(8, window.innerHeight - dialogH - safeBottom - 8)}px`,
      left: '2.5vw',
    };
  }, []);

  // Put the target in the clear space ABOVE the docked card rather than the
  // middle of the screen, which on a phone is behind it.
  const scrollTargetAbovePhoneCard = useCallback((el: HTMLElement) => {
    const y = window.scrollY + el.getBoundingClientRect().top - 100;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  }, []);

  // Measure-only function - NEVER scrolls, safe for scroll handler
  const measureTargetPosition = useCallback(() => {
    if (!currentStep?.target) {
      setTargetRect(null);
      setDialogPos({ top: '50%', left: '50%' });
      return;
    }

    const el = findVisibleTarget(currentStep.target);
    if (!el) {
      // Only wait if we haven't already given up on this step. Without the
      // flag this line defeated the 5s timeout: timeout clears the wait,
      // this effect re-runs, target is still missing, waiting re-arms -
      // "Loading..." forever on any step whose anchor never renders.
      if (currentStep.waitForElement && waitGaveUpRef.current !== step) {
        setWaitingForElement(true);
        return;
      }
      setTargetRect(null);
      setDialogPos({ top: '50%', left: '50%' });
      return;
    }

    setWaitingForElement(false);

    const rect = el.getBoundingClientRect();
    setTargetRect(rect);

    // Phones: dock the card, never overlay the target.
    const docked = phoneDockedPos();
    if (docked) { setDialogPos(docked); return; }

    const padding = 20;
    const dialogWidth = 380;
    const dialogHeight = 380;
    const safeAreaBottom = 50;
    const maxTop = window.innerHeight - dialogHeight - safeAreaBottom;

    let top = '50%';
    let left = '50%';

    switch (currentStep.position) {
      case 'bottom':
        const bottomPos = rect.bottom + padding;
        if (bottomPos > maxTop && rect.top > dialogHeight + padding + 20) {
          top = `${rect.top - dialogHeight - padding}px`;
        } else {
          top = `${Math.min(bottomPos, maxTop)}px`;
        }
        left = `${Math.max(20, Math.min(rect.left + rect.width / 2 - dialogWidth / 2, window.innerWidth - dialogWidth - 20))}px`;
        break;
      case 'top':
        const topPos = rect.top - dialogHeight - padding;
        if (topPos < 20) {
          top = `${Math.min(rect.bottom + padding, maxTop)}px`;
        } else {
          top = `${topPos}px`;
        }
        left = `${Math.max(20, Math.min(rect.left + rect.width / 2 - dialogWidth / 2, window.innerWidth - dialogWidth - 20))}px`;
        break;
      case 'left':
        top = `${Math.min(Math.max(20, rect.top + rect.height / 2 - dialogHeight / 2), maxTop)}px`;
        left = `${Math.max(20, rect.left - dialogWidth - padding)}px`;
        break;
      case 'right':
        top = `${Math.min(Math.max(20, rect.top + rect.height / 2 - dialogHeight / 2), maxTop)}px`;
        left = `${Math.min(rect.right + padding, window.innerWidth - dialogWidth - 20)}px`;
        break;
      default:
        top = '50%';
        left = '50%';
    }

    setDialogPos({ top, left });
  }, [currentStep, phoneDockedPos]);
  
  // Scroll + measure function - ONLY called on step change, never from scroll handler
  const updateTargetPosition = useCallback(() => {
    if (!currentStep?.target) {
      setTargetRect(null);
      setDialogPos({ top: '50%', left: '50%' });
      return;
    }

    const el = findVisibleTarget(currentStep.target);
    if (!el) {
      // Element not found - might still be loading. Same give-up guard as
      // above: once the timeout has fired for this step, show it centered
      // rather than re-entering the wait loop.
      if (currentStep.waitForElement && waitGaveUpRef.current !== step) {
        setWaitingForElement(true);
        return;
      }
      setTargetRect(null);
      setDialogPos({ top: '50%', left: '50%' });
      return;
    }

    setWaitingForElement(false);

    //  FIX: Only scroll once per step to prevent scroll loops

    if (hasScrolledForStepRef.current !== step) {
      hasScrolledForStepRef.current = step;
      // On a phone 'center' parks the target behind the docked card.
      if (window.innerWidth <= 820) scrollTargetAbovePhoneCard(el);
      else el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setTimeout(() => {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);

      const docked = phoneDockedPos();
      if (docked) { setDialogPos(docked); return; }

      const padding = 20;
      const dialogWidth = 380;
      const dialogHeight = 380; //  FIX: Was 250, actual height ~380px

      const safeAreaBottom = 50; //  FIX: Account for iOS safe area

      const maxTop = window.innerHeight - dialogHeight - safeAreaBottom;
      
      let top = '50%';
      let left = '50%';

      switch (currentStep.position) {
        case 'bottom':
          //  FIX: Use maxTop to prevent dialog going off screen

          const bottomPos = rect.bottom + padding;
          // If would go off bottom, try positioning above target instead
          if (bottomPos > maxTop && rect.top > dialogHeight + padding + 20) {
            top = `${rect.top - dialogHeight - padding}px`;
          } else {
            top = `${Math.min(bottomPos, maxTop)}px`;
          }
          left = `${Math.max(20, Math.min(rect.left + rect.width / 2 - dialogWidth / 2, window.innerWidth - dialogWidth - 20))}px`;
          break;
        case 'top':
          //  FIX: Use maxTop for bounds checking

          const topPos = rect.top - dialogHeight - padding;
          // If would go off top, position below target instead
          if (topPos < 20) {
            top = `${Math.min(rect.bottom + padding, maxTop)}px`;
          } else {
            top = `${topPos}px`;
          }
          left = `${Math.max(20, Math.min(rect.left + rect.width / 2 - dialogWidth / 2, window.innerWidth - dialogWidth - 20))}px`;
          break;
        case 'left':
          top = `${Math.min(Math.max(20, rect.top + rect.height / 2 - dialogHeight / 2), maxTop)}px`;
          left = `${Math.max(20, rect.left - dialogWidth - padding)}px`;
          break;
        case 'right':
          top = `${Math.min(Math.max(20, rect.top + rect.height / 2 - dialogHeight / 2), maxTop)}px`;
          left = `${Math.min(rect.right + padding, window.innerWidth - dialogWidth - 20)}px`;
          break;
        default:
          top = '50%';
          left = '50%';
      }

      // CLAMP into the viewport. 'bottom' placed the dialog above a target
      // with no lower bound, so a stale rect right after navigation computed
      // a negative top - the dialog (and its Next and Skip buttons) rendered
      // above the screen and the tour looked frozen with no way out.
      const clampPx = (v: string, max: number) => {
        if (!v.endsWith('px')) return v;
        const n = parseFloat(v);
        return `${Math.max(8, Math.min(n, max))}px`;
      };
      top = clampPx(top, window.innerHeight - 180);
      left = clampPx(left, window.innerWidth - Math.min(dialogWidth, window.innerWidth * 0.9) - 8);

      setDialogPos({ top, left });
    }, 400);
  }, [currentStep, phoneDockedPos, scrollTargetAbovePhoneCard]);

  // Flag the tour on <body> while it runs, so pages can hold their
  // auto-popups (daily bonus modal etc.) instead of fighting the overlay.
  useEffect(() => {
    if (isOpen && visible) document.body.dataset.pssTourActive = '1';
    return () => {
      delete document.body.dataset.pssTourActive;
      // The overlay stays mounted between runs - reset the one-shot
      // celebration so a replayed tour gets its ending again.
      finaleCelebratedRef.current = false;
    };
  }, [isOpen, visible]);

  // The ending screen owns its own celebration: music + confetti fire the
  // moment it appears, not when the button is pressed.
  const finaleCelebratedRef = useRef(false);
  useEffect(() => {
    if (isLast && tutorialType === 'main' && visible && !finaleCelebratedRef.current) {
      finaleCelebratedRef.current = true;
      playWinSound();
      setShowConfetti(true);
      // The ending closes ITSELF. Twice now the final dialog has been
      // unreachable while the dim and spotlight stayed up, trapping the
      // player on a frozen home screen. Completion no longer depends on
      // any button being visible: confetti falls, the music plays, and a
      // few seconds later the tour clears the screen on its own. Done just
      // closes it sooner.
      if (userId) {
        // Promise.resolve guard: the ending must survive ANY return shape
        // from the service - a throw here would freeze the celebration.
        Promise.resolve()
          .then(() => tutorialService.completeTutorial(userId, tutorialType))
          .catch(() => {});
      }
      setTimeout(() => {
        setTargetRect(null);
        setVisible(false);
        setTimeout(onComplete, 300);
      }, 4500);
    }
  }, [isLast, tutorialType, visible, userId, onComplete]);

  // Which step the wait timeout has given up on; reset when the step moves.
  const waitGaveUpRef = useRef<number | null>(null);
  useEffect(() => { waitGaveUpRef.current = null; }, [step]);

  // ==========================================================================
  // Wait for element to appear after navigation
  // ==========================================================================
  
  useEffect(() => {
    if (!waitingForElement || !currentStep?.target) return;

    const checkForElement = setInterval(() => {
      const el = findVisibleTarget(currentStep.target!);
      if (el) {
        clearInterval(checkForElement);
        setWaitingForElement(false);
        updateTargetPosition();
      }
    }, 200);

    // Timeout after 5 seconds: give up on this step's target and show the
    // step centered. The ref remembers WHICH step gave up so the measure
    // effect doesn't immediately re-enter the wait.
    const timeout = setTimeout(() => {
      clearInterval(checkForElement);
      waitGaveUpRef.current = step;
      setWaitingForElement(false);
      setTargetRect(null);
      setDialogPos({ top: '50%', left: '50%' });
    }, 5000);

    return () => {
      clearInterval(checkForElement);
      clearTimeout(timeout);
    };
  }, [waitingForElement, currentStep, updateTargetPosition]);

  // ==========================================================================
  // Effects
  // ==========================================================================

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setShowConfetti(false);
      setTimeout(() => setVisible(true), 100);
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  //  FIX: Scroll listener uses measureTargetPosition (no scroll) to prevent loop

  useEffect(() => {
    if (visible && !waitingForElement) {
      updateTargetPosition(); // Initial call with scroll
      
      //  FIX: Scroll/resize handlers use measure-only (no scrollIntoView)

      const handleUpdate = () => measureTargetPosition();
      window.addEventListener('resize', handleUpdate);
      window.addEventListener('scroll', handleUpdate, true);
      
      return () => {
        window.removeEventListener('resize', handleUpdate);
        window.removeEventListener('scroll', handleUpdate, true);
      };
    }
  }, [visible, step, updateTargetPosition, measureTargetPosition, waitingForElement, location.pathname]);

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleNext = useCallback(async () => {
    if (isLast) {
      // Complete tutorial. The finale effect above normally already played
      // the celebration on entry; only fire here if it somehow has not.
      if (!finaleCelebratedRef.current) {
        finaleCelebratedRef.current = true;
        playWinSound();
        setShowConfetti(true);
      }
      if (userId) {
        await tutorialService.completeTutorial(userId, tutorialType);
      }
      
      // Navigate to final destination if specified
      if (currentStep?.navigateTo) {
        navigate(currentStep.navigateTo);
      }
      
      setTimeout(() => {
        setVisible(false);
        setTimeout(onComplete, 300);
      }, 2000);
    } else {
      const nextStep = steps[step + 1];
      
      // Check if next step requires navigation
      if (nextStep?.navigateTo && location.pathname !== nextStep.navigateTo) {
        navigate(nextStep.navigateTo);
        // Wait a bit for navigation to complete
        setTimeout(() => {
          setStep(s => s + 1);
          if (nextStep.waitForElement) {
            setWaitingForElement(true);
          }
        }, 500);
      } else {
        setStep(s => s + 1);
      }
    }
  }, [isLast, userId, tutorialType, currentStep, navigate, location.pathname, steps, step, onComplete]);

  const handleBack = useCallback(() => {
    if (step > 0) {
      const prevStepIndex = step - 1;
      
      // Find what page the previous step should be on
      // Look backwards from prevStep to find the most recent navigateTo
      let targetPage: string | undefined;
      for (let i = prevStepIndex; i >= 0; i--) {
        if (steps[i]?.navigateTo) {
          targetPage = steps[i].navigateTo;
          break;
        }
      }
      
      // If no navigateTo found, default to home
      if (!targetPage) {
        targetPage = '/';
      }
      
      // Navigate if we're not already on the target page
      if (location.pathname !== targetPage) {
        navigate(targetPage);
        setTimeout(() => {
          setStep(prevStepIndex);
        }, 500);
      } else {
        setStep(prevStepIndex);
      }
    }
  }, [step, steps, location.pathname, navigate]);

  // Jump to specific step (for clickable progress bar)
  const handleJumpToStep = useCallback((targetStep: number) => {
    if (targetStep === step) return;
    
    const targetStepData = steps[targetStep];
    
    // If target step needs navigation, navigate first
    if (targetStepData?.navigateTo && location.pathname !== targetStepData.navigateTo) {
      navigate(targetStepData.navigateTo);
      setTimeout(() => {
        setStep(targetStep);
        if (targetStepData.waitForElement) {
          setWaitingForElement(true);
        }
      }, 500);
    } else {
      // Find if we need to navigate to a different page
      // Look backwards from target step to find the most recent navigateTo
      let pageToNavigate: string | undefined;
      for (let i = targetStep; i >= 0; i--) {
        if (steps[i]?.navigateTo) {
          pageToNavigate = steps[i].navigateTo;
          break;
        }
      }
      
      if (pageToNavigate && location.pathname !== pageToNavigate) {
        navigate(pageToNavigate);
        setTimeout(() => {
          setStep(targetStep);
          if (targetStepData?.waitForElement) {
            setWaitingForElement(true);
          }
        }, 500);
      } else {
        setStep(targetStep);
      }
    }
  }, [step, steps, location.pathname, navigate]);

  const handleSkip = useCallback(async () => {
    if (userId) {
      await tutorialService.skipTutorial(userId, tutorialType);
    }
    // Navigate back to home if not already there
    if (location.pathname !== '/') {
      navigate('/');
    }
    setVisible(false);
    setTimeout(onSkip, 300);
  }, [userId, tutorialType, location.pathname, navigate, onSkip]);

  // ==========================================================================
  // Render
  // ==========================================================================

  if (!isOpen) return null;
  
  //  FIX: Don't render on blocked paths

  if (isBlockedPath) return null;
  
  //  FIX: Don't render until DOM is ready

  if (!domReady) return null;

  const isCentered = !currentStep?.target || !targetRect;
  const isImportantStep = currentStep?.isImportant;

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex={99999}
      opacity={visible ? 1 : 0}
      transition="opacity 0.3s"
      pointerEvents={visible ? 'auto' : 'none'}
    >
      {showConfetti && <Confetti />}

      {/* The ending is the REGULAR final step dialog - confetti and the
          win music fire over it the moment it appears (effect above). */}

      {/* Escape hatch OUTSIDE the dialog. If the dialog is ever lost - a
          bad rect, a missing anchor, anything - this is the guaranteed way
          out. A tour must never be able to trap a player. */}
      <Box
        as="button"
        position="fixed"
        top="calc(env(safe-area-inset-top, 0px) + 10px)"
        right="10px"
        zIndex={100005}
        bg="rgba(3, 9, 14, 0.85)"
        border="1px solid rgba(255,255,255,0.2)"
        borderRadius="full"
        color="gray.300"
        fontSize="12px"
        fontWeight="600"
        px={3}
        py={2}
        cursor="pointer"
        _hover={{ color: 'white', borderColor: 'gray.400' }}
        onClick={handleSkip}
      >
        ✕ Exit tour
      </Box>

      {/* SPOTLIGHT. One element whose giant box-shadow is the darkness -
          the cutout stays at full brightness (the old flat wash dimmed the
          very thing being pointed at), and because it is one element moving,
          the light GLIDES between stops instead of teleporting. */}
      {targetRect ? (
        <Box
          position="fixed"
          top={`${targetRect.top - 8}px`}
          left={`${targetRect.left - 8}px`}
          width={`${targetRect.width + 16}px`}
          height={`${targetRect.height + 16}px`}
          borderRadius="18px"
          boxShadow={isImportantStep
            ? "0 0 0 9999px rgba(24, 2, 2, 0.84), inset 0 0 34px rgba(255,68,68,0.25)"
            : "0 0 0 9999px rgba(2, 7, 12, 0.84), inset 0 0 34px rgba(0,217,217,0.22)"
          }
          transition="all 0.7s cubic-bezier(0.45, 0.05, 0.2, 1)"
          pointerEvents="none"
          zIndex={99998}
        >
          <Box
            position="absolute"
            inset="-7px"
            borderRadius="24px"
            border="2px solid"
            borderColor={isImportantStep ? "rgba(255,107,107,0.8)" : "rgba(0,217,217,0.75)"}
            animation={`${pulse} 1.8s ease-in-out infinite`}
          />
        </Box>
      ) : (
        <Box
          position="absolute"
          inset={0}
          bg={isImportantStep
            ? "radial-gradient(ellipse at 50% 30%, rgba(45,10,10,0.92), rgba(24,2,2,0.96))"
            : "radial-gradient(ellipse at 50% 30%, rgba(10,31,46,0.92), rgba(2,7,12,0.96))"}
          pointerEvents="none"
          transition="background 0.5s"
        />
      )}

      {/* Loading indicator when waiting for element */}
      {waitingForElement && (
        <Box
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          color="cyan.400"
          fontSize="lg"
        >
          Loading...
        </Box>
      )}

      {/* Dialog */}
      {!waitingForElement && (
        <Box
          ref={dialogRef}
          position="fixed"
          top={dialogPos.top}
          left={dialogPos.left}
          transform={isCentered ? 'translate(-50%, -50%)' : 'none'}
          maxW={{ base: "95vw", sm: "400px" }}
          w={{ base: "95vw", sm: "90vw" }}
          zIndex={100001}
          animation={`${bounceIn} 0.4s ease-out`}
          key={step}
        >
          <Box
            bg={isImportantStep
              ? "linear-gradient(180deg, #2a0e12 0%, #160608 100%)"
              : "linear-gradient(180deg, #0E2A3C 0%, #081A27 100%)"
            }
            border="1px solid"
            borderColor={isImportantStep ? "rgba(255,107,107,0.45)" : "#1B4055"}
            borderRadius="16px"
            overflow="hidden"
            boxShadow="0 24px 60px rgba(0,0,0,0.65)"
            maxH={{ base: "calc(100vh - 60px)", md: "calc(100vh - 100px)" }}
            display="flex"
            flexDirection="column"
          >
            {/* Progress bar */}
            <Box h="4px" bg="gray.800">
              <Box
                h="100%"
                bg={isImportantStep 
                  ? "linear-gradient(90deg, #FF4444, #FF6B6B)"
                  : "linear-gradient(90deg, #00D9D9, #00FFD1)"
                }
                w={`${((step + 1) / steps.length) * 100}%`}
                transition="width 0.4s ease"
              />
            </Box>

            {/*  TOP NAVIGATION - Always visible */}

            <HStack justify="space-between" px={3} py={2} bg="rgba(0,0,0,0.3)" borderBottom="1px solid" borderColor="gray.800">
              <Box
                as="button"
                px={3}
                py={2}
                minH="44px"
                minW="70px"
                bg="transparent"
                border="none"
                borderRadius="lg"
                color={isFirst ? "gray.600" : "gray.400"}
                fontWeight="bold"
                fontSize="sm"
                cursor={isFirst ? "default" : "pointer"}
                onClick={isFirst ? undefined : handleBack}
                _hover={isFirst ? {} : { color: 'white' }}
              >
                ← Back
              </Box>
              
              <Text color={isImportantStep ? "red.400" : "cyan.400"} fontSize="xs" fontWeight="bold">
                {step + 1} / {steps.length}
              </Text>

              <Box
                as="button"
                px={3}
                py={2}
                minH="44px"
                minW="70px"
                bg={isImportantStep
                  ? "linear-gradient(135deg, #FF4444, #FF6B6B)"
                  : "linear-gradient(135deg, #ffe680, #FFD700 60%, #b9930d)"
                }
                border="none"
                borderRadius="full"
                color={isImportantStep ? "white" : "#241c00"}
                fontWeight="bold"
                fontSize="sm"
                cursor="pointer"
                _hover={{ opacity: 0.9 }}
                _active={{ transform: 'scale(0.95)' }}
                onClick={handleNext}
                disabled={showConfetti}
              >
                {isLast ? "Done " : "Next →"}

              </Box>
            </HStack>

            {/* Header - Mobile Optimized */}
            <Box 
              p={{ base: 3, md: 4 }}
              bg={isImportantStep ? "rgba(255, 0, 0, 0.1)" : "rgba(0,217,217,0.1)"} 
              borderBottom="1px solid" 
              borderColor={isImportantStep ? "red.900" : "cyan.900"}
            >
              <HStack spacing={{ base: 3, md: 4 }}>
                <Box display={{ base: "none", sm: "block" }}>
                  <ChipMascot expression={currentStep?.chipExpression} size={65} />
                </Box>
                <Box display={{ base: "block", sm: "none" }}>
                  <ChipMascot expression={currentStep?.chipExpression} size={50} />
                </Box>
                <VStack align="start" spacing={0.5} flex={1}>
                  <HStack spacing={2}>
                    {currentStep?.icon && <Text fontSize={{ base: "lg", md: "xl" }}>{currentStep.icon}</Text>}
                    <Text
                      color={isImportantStep ? "red.400" : "cyan.400"}
                      fontSize="2xs"
                      fontWeight="bold"
                      textTransform="uppercase"
                      letterSpacing="0.2em"
                    >
                      {isImportantStep ? 'Important' : `Step ${step + 1} of ${steps.length}`}
                    </Text>
                  </HStack>
                  <Text
                    color="#E8F4F8"
                    fontSize={{ base: "lg", md: "xl" }}
                    fontFamily="Georgia, 'Times New Roman', serif"
                    fontWeight="normal"
                    lineHeight="1.25"
                  >
                    {currentStep?.title}
                  </Text>
                </VStack>
              </HStack>
            </Box>

            {/* Content - Mobile Optimized */}
            <Box p={{ base: 3, md: 4 }} flex={1} overflowY="auto">
              <Text 
                color="gray.300" 
                fontSize={{ base: "sm", md: "md" }}
                lineHeight={{ base: "1.6", md: "1.7" }}
              >
                {currentStep?.content}
              </Text>
            </Box>

            {/* Section Navigation Bar - main tour only: its startIndex map
                belongs to MAIN_TUTORIAL_STEPS, and on the agent tour those
                indices point past the end of the steps array. */}
            {tutorialType === 'main' && (
            <Box px={2} pb={2}>
              {/* Section Icons - Scrollable on mobile */}
              <Box 
                overflowX="auto" 
                pb={2}
                css={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <HStack 
                  justify={{ base: "flex-start", md: "space-between" }}
                  spacing={{ base: 1, md: 2 }}
                  minW={{ base: "max-content", md: "auto" }}
                  px={1}
                >
                  {TUTORIAL_SECTIONS.map((section, i) => {
                    const isActive = step >= section.startIndex && 
                      (i === TUTORIAL_SECTIONS.length - 1 || step < TUTORIAL_SECTIONS[i + 1].startIndex);
                    const isCompleted = i < TUTORIAL_SECTIONS.length - 1 && step >= TUTORIAL_SECTIONS[i + 1].startIndex;
                    
                    return (
                      <Box
                        key={section.name}
                        as="button"
                        onClick={() => handleJumpToStep(section.startIndex)}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        opacity={isActive ? 1 : isCompleted ? 0.8 : 0.4}
                        _hover={{ opacity: 1, transform: 'scale(1.05)' }}
                        _active={{ transform: 'scale(0.95)' }}
                        transition="all 0.2s"
                        bg={isActive ? 'rgba(0, 217, 217, 0.15)' : 'transparent'}
                        border="none"
                        borderRadius="lg"
                        p={{ base: 2, md: 1 }}
                        minW={{ base: "52px", md: "auto" }}
                        minH="44px"
                        flexShrink={0}
                      >
                        <Text 
                          fontSize={{ base: "xl", md: "lg" }}
                          lineHeight={1}
                        >
                          {section.icon}
                        </Text>
                        <Text 
                          fontSize={{ base: "2xs", md: "2xs" }}
                          color={isActive ? 'cyan.400' : isCompleted ? 'green.400' : 'gray.500'}
                          fontWeight={isActive ? 'bold' : 'normal'}
                          mt={0.5}
                          display={{ base: isActive ? "block" : "none", sm: "block" }}
                        >
                          {section.name}
                        </Text>
                        {/* Active indicator dot */}
                        {isActive && (
                          <Box
                            w="4px"
                            h="4px"
                            borderRadius="full"
                            bg="cyan.400"
                            mt={1}
                            display={{ base: "block", sm: "none" }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </HStack>
              </Box>
              
              {/* Compact Progress Bar for Mobile */}
              <Box px={1}>
                {/* Section progress indicator */}
                <HStack spacing={1} justify="center">
                  {TUTORIAL_SECTIONS.map((section, i) => {
                    const nextSection = TUTORIAL_SECTIONS[i + 1];
                    const sectionEnd = nextSection ? nextSection.startIndex - 1 : steps.length - 1;
                    const sectionSteps = sectionEnd - section.startIndex + 1;
                    const stepsCompleted = Math.max(0, Math.min(step - section.startIndex + 1, sectionSteps));
                    const progress = (stepsCompleted / sectionSteps) * 100;
                    const isCurrentSection = step >= section.startIndex && step <= sectionEnd;
                    
                    return (
                      <Box
                        key={section.name}
                        flex={1}
                        h={{ base: "6px", md: "4px" }}
                        bg="gray.800"
                        borderRadius="full"
                        overflow="hidden"
                        cursor="pointer"
                        onClick={() => handleJumpToStep(section.startIndex)}
                        _hover={{ opacity: 0.8 }}
                        position="relative"
                      >
                        <Box
                          h="100%"
                          w={`${Math.min(100, progress)}%`}
                          bg={progress >= 100 ? 'green.400' : isCurrentSection ? 'cyan.400' : 'gray.600'}
                          borderRadius="full"
                          transition="width 0.3s ease"
                        />
                      </Box>
                    );
                  })}
                </HStack>
                
                {/* Current section label - mobile only */}
                <Text 
                  fontSize="2xs" 
                  color="gray.500" 
                  textAlign="center" 
                  mt={1}
                  display={{ base: "block", md: "none" }}
                >
                  {getSectionForStep(step).name} • Tap sections above to jump
                </Text>
              </Box>
            </Box>
            )}

            {/* Bottom - just Skip option */}
            <HStack 
              p={3}
              justify="center"
              borderTop="1px solid" 
              borderColor="gray.800" 
              bg="rgba(0,0,0,0.2)"
            >
              <Box
                as="button"
                px={4}
                py={2}
                bg="transparent"
                border="none"
                color="gray.500"
                fontWeight="medium"
                fontSize="sm"
                cursor="pointer"
                _hover={{ color: 'gray.300' }}
                onClick={handleSkip}
              >
                Skip Tutorial
              </Box>
            </HStack>
          </Box>

          {/* Arrow pointing to target */}
          {targetRect && currentStep?.position === 'bottom' && (
            <Box
              position="absolute"
              top="-12px"
              left="50%"
              transform="translateX(-50%)"
              w={0}
              h={0}
              borderLeft="12px solid transparent"
              borderRight="12px solid transparent"
              borderBottom="12px solid"
              borderBottomColor={isImportantStep ? "red.500" : "cyan.500"}
            />
          )}
          {targetRect && currentStep?.position === 'top' && (
            <Box
              position="absolute"
              bottom="-12px"
              left="50%"
              transform="translateX(-50%)"
              w={0}
              h={0}
              borderLeft="12px solid transparent"
              borderRight="12px solid transparent"
              borderTop="12px solid"
              borderTopColor={isImportantStep ? "red.500" : "cyan.500"}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default TutorialOverlay;